// ─────────────────────────────────────────────────────────────────
// Glass-i — HRMS Hub (module landing page).
// The flagship of the redesigned HRMS look: a hero band with live
// organization KPIs (from the org-chart endpoint) + a grid of module
// cards that navigate to each HRMS surface. Read-only; degrades
// gracefully when the KPI feed is unavailable.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import {
  TeamOutlined, PartitionOutlined, SwapOutlined, HistoryOutlined,
  SolutionOutlined, BankOutlined, ApartmentOutlined, IdcardOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';

import { hrOrganizationApi } from '../organization/hrOrganizationApi';
import '../hrms.css';

const KPI_DEFS = [
  { key: 'employees', label: 'Employees' },
  { key: 'activeEmployees', label: 'Active' },
  { key: 'salespersons', label: 'Salespersons' },
  { key: 'branches', label: 'Branches' },
  { key: 'departments', label: 'Departments' },
  { key: 'designations', label: 'Designations' }
];

export default function HrmsHubPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await hrOrganizationApi.chart();
      setSummary((data && data.summary) || null);
    } catch (_e) {
      setSummary(null); // KPIs are a nicety; the hub still works without them
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const n = useCallback((k) => (summary && typeof summary[k] === 'number' ? summary[k] : null), [summary]);

  const modules = useMemo(() => ([
    { key: 'people', title: 'People', desc: 'Directory of employees — profiles, employment, sales config, documents & contacts.', icon: <TeamOutlined />, to: '/erp/hrms/employees', count: n('employees') },
    { key: 'org', title: 'Organization Chart', desc: 'Branch, department & manager hierarchy with live head-counts.', icon: <PartitionOutlined />, to: '/erp/hrms/organization-chart', count: n('activeEmployees') },
    { key: 'transfers', title: 'Employee Transfers', desc: 'Move an employee’s branch, department, designation or manager — draft then approve.', icon: <SwapOutlined />, to: '/erp/hrms/employee-transfers' },
    { key: 'history', title: 'Employee History', desc: 'Append-only change log — transfers, status, login & salesperson lifecycle.', icon: <HistoryOutlined />, to: '/erp/hrms/employee-history' },
    { key: 'salesmap', title: 'Salesperson Mapping', desc: 'Assign customers to their HRMS salesperson and review assignment history.', icon: <SolutionOutlined />, to: '/erp/hrms/salesperson-mapping', count: n('salespersons') },
    { key: 'branches', title: 'Branches', desc: 'Operating locations that employees are posted to.', icon: <BankOutlined />, to: '/erp/hrms/branches', count: n('branches') },
    { key: 'departments', title: 'Departments', desc: 'Functional units used across the organization.', icon: <ApartmentOutlined />, to: '/erp/hrms/departments', count: n('departments') },
    { key: 'designations', title: 'Designations', desc: 'Roles & the reporting / approval hierarchy of titles.', icon: <IdcardOutlined />, to: '/erp/hrms/designations', count: n('designations') }
  ]), [n]);

  const go = (to) => navigate(to);

  return (
    <div className="hr-shell">
      <div className="hr-hub">
        {/* Hero band with live KPIs */}
        <div className="hr-hub-hero">
          <div className="hr-hub-hero__eyebrow">Human Resources</div>
          <div className="hr-hub-hero__title">People &amp; Organization</div>
          <div className="hr-hub-hero__subtitle">
            Your team, structure and salesforce in one place — manage employees, map the org,
            move people between roles, and keep an auditable trail of every change.
          </div>

          <Spin spinning={loading}>
            <div className="hr-hub-kpis">
              {KPI_DEFS.map((k) => (
                <div key={k.key} className="hr-hub-kpi">
                  <div className="hr-hub-kpi__value">{n(k.key) != null ? n(k.key) : '—'}</div>
                  <div className="hr-hub-kpi__label">{k.label}</div>
                </div>
              ))}
            </div>
          </Spin>
        </div>

        <div className="hr-hub-section">Modules</div>

        <div className="hr-module-grid">
          {modules.map((m) => (
            <div
              key={m.key}
              className="hr-module-card"
              role="button"
              tabIndex={0}
              onClick={() => go(m.to)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(m.to); } }}
            >
              <div className="hr-module-card__top">
                <span className="hr-module-card__icon">{m.icon}</span>
                {m.count != null ? <span className="hr-module-card__count">{m.count}</span> : null}
              </div>
              <div className="hr-module-card__title">{m.title}</div>
              <div className="hr-module-card__desc">{m.desc}</div>
              <span className="hr-module-card__cta">Open <ArrowRightOutlined style={{ fontSize: 11 }} /></span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
